/**
 * Velucia DE : comportements des sections et blocs « velucia-de-* ».
 *
 * - Recopie l'offre choisie dans le bundle (bloc « Velucia DE · Bundle ») partout où la page
 *   l'affiche : rappels d'achat, barre mobile, contenu du colis.
 * - Les boutons « In den Warenkorb » hors bundle déclenchent le bouton du bundle : un seul
 *   chemin d'ajout au panier, celui du thème (tiroir panier compris).
 * - Onglets avant/après, lecteur des vidéos UGC, barre d'achat mobile.
 *
 * Chargé en module : exécuté une seule fois même si plusieurs sections l'appellent.
 * Tout passe par délégation d'événements, donc survit aux rechargements de sections de l'éditeur.
 */
const BIND_ATTR = 'data-vlde-bind';

function getBundle() {
  return document.querySelector('[data-vlde-bundle]');
}

function selectedOffer(bundle) {
  if (!bundle) return null;
  return bundle.querySelector('[data-bp-offer][aria-checked="true"]') || bundle.querySelector('[data-bp-offer]');
}

function syncFromBundle() {
  const offer = selectedOffer(getBundle());
  if (!offer) return;
  const values = {
    label: offer.dataset.vldeLabel || '',
    price: offer.dataset.totalOnce || '',
    per: offer.dataset.vldePer || '',
    contents: offer.dataset.vldeContents || '',
    summary: offer.dataset.vldeSummary || '',
  };
  for (const node of document.querySelectorAll(`[${BIND_ATTR}]`)) {
    const key = node.getAttribute(BIND_ATTR);
    if (key && key in values && node.textContent !== values[key]) node.textContent = values[key];
  }
}

let observedBundle = null;
const bundleObserver = new MutationObserver(() => syncFromBundle());

function watchBundle() {
  const bundle = getBundle();
  if (bundle === observedBundle) return;
  bundleObserver.disconnect();
  observedBundle = bundle;
  if (bundle) {
    bundleObserver.observe(bundle, { subtree: true, attributes: true, attributeFilter: ['aria-checked'] });
  }
  syncFromBundle();
}

function bundleButton() {
  const bundle = getBundle();
  return bundle ? bundle.querySelector('[data-bp-cta] button') : null;
}

/* Ajout au panier depuis les rappels et la barre mobile */
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-vlde-add]') : null;
  if (!trigger) return;
  event.preventDefault();
  const button = bundleButton();
  if (button && !button.disabled) {
    button.click();
    return;
  }
  const anchor = document.getElementById('angebot');
  if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

/* Onglets (avant/après) */
function activateTab(tab, focus) {
  const list = tab.closest('[role="tablist"]');
  if (!list) return;
  const tabs = Array.from(list.querySelectorAll('[role="tab"]'));
  for (const item of tabs) {
    const selected = item === tab;
    item.setAttribute('aria-selected', selected ? 'true' : 'false');
    item.tabIndex = selected ? 0 : -1;
    const panel = document.getElementById(item.getAttribute('aria-controls') || '');
    if (panel) panel.hidden = !selected;
  }
  if (focus) tab.focus();
}

document.addEventListener('click', (event) => {
  const tab = event.target instanceof Element ? event.target.closest('.vlde [role="tab"]') : null;
  if (tab) activateTab(tab, false);
});

document.addEventListener('keydown', (event) => {
  const tab = event.target instanceof Element ? event.target.closest('.vlde [role="tab"]') : null;
  if (!tab) return;
  const tabs = Array.from(tab.closest('[role="tablist"]')?.querySelectorAll('[role="tab"]') || []);
  const index = tabs.indexOf(tab);
  let next = null;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = tabs[(index + 1) % tabs.length];
  if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = tabs[(index - 1 + tabs.length) % tabs.length];
  if (event.key === 'Home') next = tabs[0];
  if (event.key === 'End') next = tabs[tabs.length - 1];
  if (next) {
    event.preventDefault();
    activateTab(next, true);
  }
});

/* Vidéos UGC : une seule vidéo à la fois, arrêtée à la fermeture */
function stopVideo(dialog) {
  const video = dialog.querySelector('video');
  if (!video) return;
  video.pause();
  video.removeAttribute('src');
  for (const source of video.querySelectorAll('source')) source.remove();
  video.load();
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const tile = event.target.closest('[data-vlde-video]');
  if (tile) {
    const block = tile.closest('.vlde');
    const dialog = block ? block.querySelector('[data-vlde-video-dialog]') : null;
    const video = dialog ? dialog.querySelector('video') : null;
    if (!dialog || !video || typeof dialog.showModal !== 'function') return;
    stopVideo(dialog);
    const source = document.createElement('source');
    source.src = tile.dataset.vldeVideo || '';
    source.type = tile.dataset.vldeType || 'video/mp4';
    video.appendChild(source);
    video.load();
    dialog.showModal();
    video.play().catch(() => {});
    return;
  }
  const close = event.target.closest('[data-vlde-close]');
  if (close) {
    const dialog = close.closest('dialog');
    if (dialog) dialog.close();
    return;
  }
  if (event.target.matches('[data-vlde-video-dialog]')) {
    /* clic sur le fond, hors de la vidéo */
    event.target.close();
  }
});

document.addEventListener(
  'close',
  (event) => {
    if (event.target instanceof Element && event.target.matches('[data-vlde-video-dialog]')) stopVideo(event.target);
  },
  true
);

/* Carrousel « Ergebnisse » : chaque flèche fait défiler d'une carte, et repart au début en fin de liste */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-vlde-zurueck], [data-vlde-weiter]') : null;
  if (!button) return;
  const spur = button.closest('[data-vlde-karussell]')?.querySelector('[data-vlde-spur]');
  if (!spur) return;
  const karte = spur.querySelector('li');
  const luecke = parseFloat(getComputedStyle(spur).columnGap) || 0;
  const schritt = karte ? karte.getBoundingClientRect().width + luecke : spur.clientWidth;
  const weiter = button.hasAttribute('data-vlde-weiter');
  const ende = spur.scrollWidth - spur.clientWidth - 2;
  if (weiter && spur.scrollLeft >= ende) spur.scrollTo({ left: 0 });
  else if (!weiter && spur.scrollLeft <= 2) spur.scrollTo({ left: spur.scrollWidth });
  else spur.scrollBy({ left: weiter ? schritt : -schritt });
});

/* Barre d'achat mobile : visible quand le bouton du bundle est passé au-dessus de l'écran */
function updateBar() {
  const bar = document.querySelector('[data-vlde-kaufleiste]');
  if (!bar) {
    document.body.classList.remove('vlde-has-barre');
    return;
  }
  const anchor = document.querySelector('[data-vlde-anchor]');
  const show = Boolean(anchor) && anchor.getBoundingClientRect().bottom < 0;
  if (bar.hidden === show) bar.hidden = !show;
  document.body.classList.toggle('vlde-has-barre', show);
}

let barTimer = null;
function scheduleBar() {
  if (barTimer) return;
  barTimer = setTimeout(() => {
    barTimer = null;
    updateBar();
  }, 100);
}

window.addEventListener('scroll', scheduleBar, { passive: true });
window.addEventListener('resize', scheduleBar, { passive: true });

/* Animation de la section « Anwenderstudie » : arrêtée quand le système demande moins d'animations */
function respectReducedMotion() {
  if (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  for (const video of document.querySelectorAll('.vlde .studie__medien video')) {
    video.removeAttribute('autoplay');
    video.pause();
    video.controls = true;
  }
}

function init() {
  watchBundle();
  updateBar();
  respectReducedMotion();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

/* Éditeur de thème : les sections rechargées ou déplacées doivent être reprises */
for (const name of ['shopify:section:load', 'shopify:section:reorder', 'shopify:block:select']) {
  document.addEventListener(name, () => requestAnimationFrame(init));
}
